// Het vlak Wachtrij: wat op een besluit van de beheerder wacht (W9).
//
// Verhuisd uit BeheerPagina.tsx toen Beheer acht vlakken kreeg (W6, besluit
// V1). De lijst zelf haalt de schil op, want die toont het aantal ook in de
// paginakop; twee aanroepen voor hetzelfde getal zouden uit elkaar kunnen
// lopen.
//
// Het krediet rekent dit vlak niet uit: "3e van 5" komt uit de API, geteld uit
// goedgekeurde verzoeken. Een teller in het scherm zou kunnen afwijken.
//
// W6 ronde 2 voegde drie dingen toe, alle drie om het besluit HIER te nemen in
// plaats van op een ander scherm:
//  · een uitzondering wordt hier afgevinkt. Tot nu toe verwees de rij door.
//    Sinds review ronde 2 via een eigen beheerroute: WIE afvinkt bepaalt de
//    server (de ingelogde beheerder), niet de browser — die stuurt alleen een
//    notitie. Daarom heeft dit vlak het eigen e-mailadres niet meer nodig.
//  · bij een onderwerp-verzoek staat wat er al bestaat en wat het kost
//    (V2-PRD §11.2, "suggest existing"). Een hint van de server, geen oordeel.
//  · een kredietverzoek ("meer aanvragen", V2-PRD §11.1) zegt wat het vraagt,
//    en bij Goedkeuren kan de beheerder meteen een nieuw maximum meegeven.

import { useId, useState } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { ApiError, api } from "../../lib/api";
import type { Resource } from "../../lib/useResource";
import type {
  BesluitRequest,
  BestaatAl,
  Raming,
  WachtrijItem,
  WachtrijResponse,
} from "../../contracts";
import { color, size, weight } from "../../tokens";
import { DollarIcoon, VinkIcoon } from "./Iconen";
import { invoer, knopDonker, knopLicht } from "./stijl";
// De foutzin deelt dit vlak met Bronnen & dekking: één plek, één afspraak
// over wanneer de tekst van de server leesbaar is (review W6 ronde 3).
import { kredietLabel, leesbaar } from "./woorden";

export function Wachtrij({ wachtrij }: { wachtrij: Resource<WachtrijResponse> }) {
  // Een zin die de rij moet overleven (review ronde 2). Vinkte iemand anders
  // de uitzondering al af, dan haalt de lijst zich opnieuw op en verdwijnt de
  // rij — de uitleg van de server blijft dan hier staan, boven de lijst.
  const [melding, setMelding] = useState<string | null>(null);
  const vol = Boolean(wachtrij.data?.items?.length);

  const naBesluit = (zin?: string) => {
    setMelding(zin ?? null);
    wachtrij.reload();
  };

  return (
    <Card style={{ marginBottom: 28, padding: vol ? 0 : 18 }}>
      {melding && (
        <p
          role="status"
          style={{
            fontSize: size.meta,
            color: color.faint,
            lineHeight: 1.5,
            padding: vol ? "12px 18px 0" : 0,
            marginBottom: vol ? 0 : 8,
          }}
        >
          {melding}
        </p>
      )}
      {wachtrij.error ? (
        <LoadError reason={wachtrij.error} onRetry={wachtrij.reload} />
      ) : wachtrij.loading ? (
        <EmptyReason reason="de wachtrij wordt opgehaald" />
      ) : !wachtrij.data?.items.length ? (
        <EmptyReason reason="er wacht niets op een besluit" />
      ) : (
        wachtrij.data.items.map((item, i) => (
          <WachtrijRij
            key={item.id}
            item={item}
            eerste={i === 0}
            naBesluit={naBesluit}
          />
        ))
      )}
    </Card>
  );
}

/** "1 trend", "110 trends" — getallen met Nederlandse duizendtallen. */
function aantal(n: number, een: string, meer: string): string {
  return `${n.toLocaleString("nl-NL")} ${n === 1 ? een : meer}`;
}

/** Bovengrens voor een nieuw maximum per maand (review ronde 2). */
const MAXIMUM_GRENS = 100_000;

const MAXIMUM_REDEN =
  "Vul een heel getal in van 0 tot en met 100.000 — of laat het veld leeg, dan blijft het maximum wat het is.";

/**
 * Het nieuwe maximum uit het veld. Leeg = niets meesturen ("het maximum
 * blijft"); anders alleen een heel getal van 0 tot en met 100.000.
 *
 * Waarom het scherm dit wél zelf nakijkt (review ronde 2): een getalveld laat
 * "2.5" en "1e9" door, en de server antwoordt daarop met een Engelstalige
 * validatielijst in plaats van een reden. Dit is een vormcontrole op het veld,
 * geen nagebouwde regel over wie hoeveel krediet mag — dat blijft de server.
 */
function leesMaximum(tekst: string): { waarde: number | null; fout: string | null } {
  const t = tekst.trim();
  if (t === "") return { waarde: null, fout: null };
  const n = Number(t);
  if (!Number.isInteger(n) || n < 0 || n > MAXIMUM_GRENS) {
    return { waarde: null, fout: MAXIMUM_REDEN };
  }
  return { waarde: n, fout: null };
}

function WachtrijRij({
  item,
  eerste,
  naBesluit,
}: {
  item: WachtrijItem;
  eerste: boolean;
  /** Lijst opnieuw ophalen; met een zin als die boven de lijst moet blijven staan. */
  naBesluit: (zin?: string) => void;
}) {
  const [motivering, setMotivering] = useState("");
  const [notitie, setNotitie] = useState("");
  const [nieuwMaximum, setNieuwMaximum] = useState("");
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const maximumRedenId = useId();

  const aanvrager = item.aanvrager as { naam?: string | null; email?: string } | null;
  const krediet = item.krediet as { maximum: number; gebruikt: number } | null;
  const isKrediet = item.herkomst === "verzoek" && item.soort === "krediet";
  const vraagt = isKrediet
    ? (item.payload as { soort?: unknown; aantal?: unknown } | undefined)
    : undefined;

  const maximum = isKrediet ? leesMaximum(nieuwMaximum) : { waarde: null, fout: null };
  // Owner 06-10 (K2): een afgeschermd document vraagt wél ja of nee — telt het
  // mee in de weging? De server zet `documentId` in het wachtrij-item.
  const afgeschermd =
    item.herkomst === "uitzondering"
      ? (item.payload as { documentId?: unknown; url?: unknown } | undefined)
      : undefined;
  const documentId = typeof afgeschermd?.documentId === "string" ? afgeschermd.documentId : null;

  async function beslis(besluit: "goedgekeurd" | "afgewezen") {
    const body: BesluitRequest = { besluit, motivering: motivering.trim() || null };
    // Alleen bij ja op een kredietverzoek, en alleen als er iets is ingevuld:
    // leeg betekent "het maximum blijft wat het is". Bij nee speelt het veld
    // geen rol, dus ook een onleesbaar getal houdt een afwijzing niet tegen.
    if (isKrediet && besluit === "goedgekeurd") {
      // De reden staat al onder het veld; niets versturen (review ronde 2).
      if (maximum.fout) return;
      if (maximum.waarde !== null) body.nieuwMaximum = maximum.waarde;
    }
    setBezig(true);
    setFout(null);
    try {
      await api.besluit(item.id, body);
      naBesluit();
    } catch (err) {
      setFout(leesbaar(err, "Het besluit kon niet worden opgeslagen."));
      setBezig(false);
    }
  }

  async function besluitToegang(besluit: "toegestaan" | "afgewezen") {
    if (!documentId) return;
    setBezig(true);
    setFout(null);
    try {
      await api.toegangBesluit(documentId, { besluit, notitie: notitie.trim() || null });
      naBesluit(
        besluit === "toegestaan"
          ? "Toegestaan: het document telt mee vanaf de volgende herberekening."
          : "Afgewezen: het document blijft buiten de weging; de citaten blijven zichtbaar.",
      );
    } catch (err) {
      setFout(leesbaar(err, "Het besluit kon niet worden opgeslagen."));
      setBezig(false);
    }
  }

  async function vinkAf() {
    setBezig(true);
    setFout(null);
    try {
      // Geen naam uit de browser: de server zet de ingelogde beheerder erop.
      await api.afvinken(item.id, { notitie: notitie.trim() || null });
      naBesluit();
    } catch (err) {
      // 404 = staat niet meer open (iemand anders was sneller). Geen fout van
      // deze beheerder: lijst verversen en de zin van de server tonen.
      if (err instanceof ApiError && err.status === 404) {
        naBesluit(leesbaar(err, "Deze uitzondering staat niet meer open."));
        return;
      }
      setFout(leesbaar(err, "Het afvinken kon niet worden opgeslagen."));
      setBezig(false);
    }
  }

  return (
    <div
      style={{
        padding: 18,
        borderTop: eerste ? "none" : `1px solid ${color.lineFaint}`,
        fontSize: size.meta,
        color: color.body,
        // Een titel of omschrijving met een lange URL (de afgeschermde
        // documenten) breekt af in plaats van het scherm op 390 te verbreden.
        overflowWrap: "anywhere",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink }}>
            {item.titel}
          </div>
          <div style={{ color: color.faint, marginTop: 2 }}>
            {item.herkomst === "verzoek" ? "verzoek" : "uitzondering"}
            {item.soort ? ` · ${item.soort}` : ""}
            {aanvrager?.email ? ` · ${aanvrager.naam || aanvrager.email}` : ""}
            {/* Context bij het besluit, geen automatische poort (W9/B4). Een
                kredietverzoek kost zelf geen krediet, dus daar is het de
                huidige stand en niet "de hoeveelste". */}
            {krediet
              ? isKrediet
                ? ` · nu ${krediet.gebruikt} van ${krediet.maximum} gebruikt deze maand`
                : ` · ${krediet.gebruikt + 1}e van ${krediet.maximum} deze maand`
              : ""}
          </div>
          {item.omschrijving && (
            <p style={{ color: color.muted, marginTop: 8, lineHeight: 1.5 }}>
              {item.omschrijving}
            </p>
          )}
          {isKrediet &&
            (typeof vraagt?.aantal === "number" && typeof vraagt?.soort === "string" ? (
              <p style={{ color: color.ink, fontWeight: weight.medium, marginTop: 8 }}>
                {/* Dezelfde woorden als het kredietblok in de workspace
                    (review ronde 2), midden in een zin dus zonder hoofdletter. */}
                {`Vraagt +${vraagt.aantal} ${kredietLabel(vraagt.soort).toLowerCase()}`}
              </p>
            ) : (
              <div style={{ marginTop: 8 }}>
                <EmptyReason reason="het verzoek zegt niet hoeveel krediet het vraagt" />
              </div>
            ))}
          {item.bestaatAl && <BestaatAlRegels bestaatAl={item.bestaatAl} />}
          {item.raming && <RamingRegel raming={item.raming} />}
          {item.kostenIndicatie && (
            <p style={{ color: color.faint, marginTop: 6 }}>Kosten: {item.kostenIndicatie}</p>
          )}
        </div>
      </div>

      {item.besluitbaar ? (
        <>
          <div
            style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}
          >
            {/* Een placeholder verdwijnt zodra je typt en wordt door een
                schermlezer niet altijd voorgelezen; het aria-label blijft
                (review ronde 2). */}
            <input
              type="text"
              value={motivering}
              onChange={(e) => setMotivering(e.target.value)}
              placeholder="Waarom ja of nee? (gaat mee met het besluit)"
              aria-label="Motivering bij het besluit"
              style={{ ...invoer, flex: 1, minWidth: 220 }}
            />
            {isKrediet && (
              <input
                type="number"
                min={0}
                max={MAXIMUM_GRENS}
                step={1}
                inputMode="numeric"
                value={nieuwMaximum}
                onChange={(e) => setNieuwMaximum(e.target.value)}
                placeholder="nieuw maximum"
                aria-label="nieuw maximum"
                aria-invalid={maximum.fout ? true : undefined}
                aria-describedby={maximum.fout ? maximumRedenId : undefined}
                title="Optioneel: het nieuwe maximum per maand voor deze persoon. Leeg laten = het maximum blijft."
                style={{ ...invoer, width: 140, flex: "none" }}
              />
            )}
            <button onClick={() => void beslis("goedgekeurd")} disabled={bezig} style={knopDonker(bezig)}>
              Goedkeuren
            </button>
            <button onClick={() => void beslis("afgewezen")} disabled={bezig} style={knopLicht}>
              Afwijzen
            </button>
          </div>
          {maximum.fout && (
            <p id={maximumRedenId} style={{ color: color.accentAmber, marginTop: 8, lineHeight: 1.5 }}>
              {maximum.fout}
            </p>
          )}
        </>
      ) : documentId ? (
        <div
          style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}
        >
          <input
            type="text"
            value={notitie}
            onChange={(e) => setNotitie(e.target.value)}
            placeholder="Notitie (optioneel)"
            aria-label="Notitie bij het besluit over het afgeschermde document"
            style={{ ...invoer, flex: 1, minWidth: 220 }}
          />
          <button
            onClick={() => void besluitToegang("toegestaan")}
            disabled={bezig}
            style={knopDonker(bezig)}
          >
            Laat meetellen
          </button>
          <button onClick={() => void besluitToegang("afgewezen")} disabled={bezig} style={knopLicht}>
            Buiten de weging
          </button>
        </div>
      ) : (
        // Een uitzondering meldt iets dat gebeurd is; ze vraagt geen
        // toestemming. Dus één knop — afvinken = gezien en beoordeeld.
        <div
          style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}
        >
          <input
            type="text"
            value={notitie}
            onChange={(e) => setNotitie(e.target.value)}
            placeholder="Notitie (optioneel)"
            aria-label="Notitie bij het afvinken"
            style={{ ...invoer, flex: 1, minWidth: 220 }}
          />
          <button
            onClick={() => void vinkAf()}
            disabled={bezig}
            style={{
              ...knopDonker(bezig),
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <VinkIcoon /> Afvinken
          </button>
        </div>
      )}

      {fout && <p style={{ color: color.accentRed, marginTop: 8 }}>{fout}</p>}
    </div>
  );
}

/** Wat er al is dat erop lijkt. Beide lijsten leeg = niets tonen: een lege
 *  hint is geen informatie. */
function BestaatAlRegels({ bestaatAl }: { bestaatAl: BestaatAl }) {
  const { onderwerpen, lijktOp } = bestaatAl;
  if (!onderwerpen.length && !lijktOp.length) return null;
  return (
    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
      {onderwerpen.length > 0 && (
        <p style={{ color: color.body, lineHeight: 1.5 }}>
          {"Bestaat al als onderwerp: " +
            onderwerpen
              .map(
                (o) =>
                  `${o.naam} (${aantal(o.trends, "trend", "trends")} · ` +
                  `${aantal(o.skills, "vaardigheid", "vaardigheden")})`,
              )
              .join(", ")}
        </p>
      )}
      {lijktOp.length > 0 && (
        <p
          style={{ color: color.body, lineHeight: 1.5 }}
          title="Het getal zegt hoe sterk de titels op elkaar lijken: 0 is niet, 1 is gelijk."
        >
          {"Lijkt op: " +
            lijktOp
              .slice(0, 3)
              .map(
                (l) =>
                  `${l.titel} (${l.gelijkenis.toLocaleString("nl-NL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })})`,
              )
              .join("; ")}
        </p>
      )}
    </div>
  );
}

/** De raming: altijd de tekst van de server; een bedrag alleen als hij er een
 *  heeft — nooit een verzonnen nul. */
function RamingRegel({ raming }: { raming: Raming }) {
  return (
    <p
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: color.faint,
        marginTop: 6,
        lineHeight: 1.5,
      }}
    >
      <DollarIcoon />
      <span>
        {raming.bedragUsd !== null && (
          <strong style={{ color: color.body, fontWeight: weight.medium }}>
            {`$${raming.bedragUsd.toLocaleString("nl-NL", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} · `}
          </strong>
        )}
        {raming.tekst}
      </span>
    </p>
  );
}
